import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { brandCanvasClass } from '@/components/brand/entry-styles';
import { useStatusBarSurface } from '@/hooks/use-status-bar-surface';
import { markBootPainted } from '@/lib/mobile/boot-paint';

function markBootPaintedOnNextFrame(): void {
  requestAnimationFrame(markBootPainted);
}

/** Static launch mark matching the native storyboard and pre-React canvas. */
export function MobileBootScreen() {
  const { t } = useTranslation('common');

  // The first surface a cold launch paints, and the last one anything else
  // touches: this screen renders INSTEAD of the outlet, so no shell effect runs
  // behind it. Frozen cream in both themes, so no theme input.
  useStatusBarSurface('light');

  // Two frames: the first schedules after mount, the second only fires once
  // the first frame was committed to screen - the earliest moment the splash
  // can lift without exposing an unpainted webview.
  useEffect(() => {
    let painted = false;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => {
        painted = true;
        markBootPainted();
      });
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);

      if (!painted) {
        // The destination replaced this screen before its paint signal. Start
        // the two-frame proof again from that replacement commit: the first
        // frame paints the destination, and the second safely lifts the splash.
        requestAnimationFrame(markBootPaintedOnNextFrame);
      }
    };
  }, []);

  return (
    <main
      className={`relative grid min-h-dvh place-items-center font-sohne antialiased ${brandCanvasClass}`}
    >
      <img
        alt=""
        aria-hidden="true"
        className="block size-16"
        decoding="sync"
        fetchPriority="high"
        height={64}
        src="/mobile-boot-logo.png"
        width={64}
      />
      <output aria-label={t('mobileBoot.loading')} className="sr-only" />
    </main>
  );
}
